import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Listbox } from "@headlessui/react";
import Button from "@surgia/ui/components/button";
import Input from "../../components/input";
import mailIcon from "@surgia/ui/icons/popup/email.svg";

export interface InviteUserFormProps {
  onSuccess: (data: any) => void;
  onCancel: () => void;
  showBranches?: boolean;
  userGroups: any[];
  isLoadingUserGroups?: boolean;
  branches: any[];
  isLoadingBranches?: boolean;
  branchesLoading?: boolean; // Add this new prop
}

// Multi-select component using Headless UI
const MultiSelect: React.FC<{
  options: any[];
  selectedValues: number[];
  onSelectionChange: (values: number[]) => void;
  placeholder: string;
  isLoading?: boolean;
  label: string;
  required?: boolean;
  isSubmitted?: boolean;
}> = ({
  options,
  selectedValues,
  onSelectionChange,
  placeholder,
  isLoading = false,
  label,
  required = false,
  isSubmitted = false,
}) => {
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.id)
  );

  const handleToggle = (optionId: number) => {
    if (selectedValues.includes(optionId)) {
      onSelectionChange(selectedValues.filter((id) => id !== optionId));
    } else {
      onSelectionChange([...selectedValues, optionId]);
    }
  };

  const getDisplayText = () => {
    if (isLoading) return "Loading...";
    if (selectedValues.length === 0) return placeholder;

    const selectedNames = selectedValues
      .map((id) => options.find((option) => option.id === id)?.name)
      .filter(Boolean)
      .join(", ");

    return selectedNames || placeholder;
  };

  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-gray-700">
        {label} {required && <span className="text-blue-600">*</span>}
      </label>
      <Listbox value={selectedOptions} onChange={() => {}} multiple>
        <div className="relative">
          <Listbox.Button
            disabled={isLoading}
            className={`w-full h-11 px-3.5 py-2.5 bg-white border border-[#D5D7DA] rounded-lg shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between ${
              selectedValues.length > 0 ? "text-black" : "text-[#717680]"
            } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span
              className="truncate flex-1 mr-2 max-w-[400px]"
              title={getDisplayText()}
            >
              {getDisplayText()}
            </span>{" "}
            <svg
              className="w-4 h-4 transition-transform flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </Listbox.Button>

          <Listbox.Options className="absolute z-[9999] mt-1 w-full bg-white border border-[#D5D7DA] rounded-lg shadow-lg max-h-48 overflow-y-auto focus:outline-none transform -translate-y-0">
            {options.map((option) => (
              <Listbox.Option
                key={option.id}
                value={option}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 px-3 ${
                    active ? "bg-gray-50" : "text-gray-900"
                  } ${selectedValues.includes(option.id) ? "bg-blue-50" : ""}`
                }
                onClick={() => handleToggle(option.id)}
              >
                {({}) => (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{option.name}</span>
                    {selectedValues.includes(option.id) && (
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
      <p className="text-red-500 text-xs mt-1 min-h-[20px]">
        {isSubmitted &&
          selectedValues.length === 0 &&
          required &&
          `Please select at least one ${label.toLowerCase()}`}
      </p>
    </div>
  );
};

export const InviteUserForm: React.FC<InviteUserFormProps> = ({
  onSuccess,
  showBranches = true,
  userGroups,
  isLoadingUserGroups = false,
  branches,
  isLoadingBranches = false,
  branchesLoading = false, // Add this new prop
}) => {
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted },
    reset,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      role: "",
      branch: "",
      mobile_number_prefix: "+966",
    },
    mode: "onBlur",
  });

  const emailValue = watch("email");

  // Create a const for the disabled state
  const isSendBtnDisabled =
    isLoadingUserGroups ||
    isLoadingBranches ||
    branchesLoading ||
    isSubmitted ||
    selectedRoles.length === 0 ||
    (selectedBranches.length === 0 && showBranches) ||
    !emailValue?.trim() ||
    Object.keys(errors).length > 0;

  const onSubmit = (data: any) => {
    onSuccess({
      email: data.email,
      role: selectedRoles,
      branch: selectedBranches,
    });
  };

  const handleClear = () => {
    // Reset form values
    reset();
    setSelectedRoles([]);
    setSelectedBranches([]);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      {/* Email address */}
      <Input
        label="Email address"
        placeholder="Enter user email"
        type="email"
        icon={mailIcon}
        iconAlt="Mail"
        register={register("email", {
          required: "Email is required",
          pattern: {
            value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: "Invalid email address",
          },
        })}
        error={errors.email}
        isSubmitted={isSubmitted}
        required
      />

      {/* Role - Multi-Select using Headless UI */}
      <MultiSelect
        options={userGroups}
        selectedValues={selectedRoles}
        onSelectionChange={setSelectedRoles}
        placeholder="Select user role"
        isLoading={isLoadingUserGroups}
        label="Role"
        required
        isSubmitted={isSubmitted}
      />

      {/* Branch - Multi-Select using Headless UI */}
      {showBranches && (
        <MultiSelect
          options={branches}
          selectedValues={selectedBranches}
          onSelectionChange={setSelectedBranches}
          placeholder="Select Branch"
          isLoading={branchesLoading || isLoadingBranches} // Use both loading states
          label="Branch"
          required
          isSubmitted={isSubmitted}
        />
      )}

      {/* Form buttons */}
      <div className="flex justify-between gap-4 w-full">
        <Button
          variant="outline"
          onClick={handleClear}
          className="w-full"
          type="button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className={`w-full ${isSendBtnDisabled ? "!cursor-not-allowed" : "cursor-pointer"}`}
          type="submit"
          disabled={isSendBtnDisabled}
        >
          Send invitation
        </Button>
      </div>
    </form>
  );
};
